import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { formatBusinessDate } from '@/lib/format';
import { driverProfileName } from '@/lib/driverProfile';

export default function LoanRepaymentsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['loan-repayments-count', debouncedSearch],
        queryFn: async () => {
            let query = supabase.from('loan_repayments').select('*', { count: 'exact', head: true });
            if (debouncedSearch) {
                query = query.ilike('description', `%${debouncedSearch}%`);
            }
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({
        totalCount: countData || 0
    });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: repayments, isLoading } = useQuery({
        queryKey: ['loan-repayments', currentPage, debouncedSearch],
        queryFn: async () => {
            let query = supabase
                .from('loan_repayments')
                // `admin_id` IS SELECTED SO THE DRIVER'S OWN ROW CAN BE PICKED OUT — see
                // `driverProfileOf`; the embed can carry an admin's test profile beside it.
                .select('*, transactions(amount, transaction_at), loans(contracts(drivers(profiles(id, admin_id, first_name, last_name))))')
                .order('created_at', { ascending: false });
            if (debouncedSearch) {
                query = query.ilike('description', `%${debouncedSearch}%`);
            }
            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    const formatCurrency = (amount: number) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search by description..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
            </CardHeader>
            <CardContent>
                    <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : repayments && repayments.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Advance</TableHead>
                                <TableHead>Driver</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {repayments.map((r) => {
                                const driver = driverProfileName(r.loans?.contracts?.drivers?.profiles) ?? '-';
                                return (
                                    <TableRow key={r.id}>
                                        <TableCell>{formatBusinessDate(r.transactions?.transaction_at)}</TableCell>
                                        <TableCell>#{r.loan_id}</TableCell>
                                        <TableCell>{driver}</TableCell>
                                        <TableCell className="max-w-[200px] truncate">{r.description || '-'}</TableCell>
                                        <TableCell className="text-right font-medium">{r.transactions?.amount ? formatCurrency(r.transactions.amount) : '-'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No advance repayments found</p>
                )}
            </CardContent>
        </Card>
    );
}
